import { ThemeEvents } from '@theme/events';

/**
 * <where-to-buy> — the product page "Where to Buy" popup (design A, 2026-10-27).
 *
 * Two tabs: "Buy online" lists the retailers that stock the selected finish (rows linking to
 * that finish on the retailer's site), "Find a stockist" lazy-loads the Progus store-locator map.
 *
 * Retailers come from a denormalised JSON map (keyed by variant id) the middleware writes into
 * the product metafield `custom.stockist_urls`. Payload per variant id:
 *   [{ retailer, name, logo, regions: ["uk"|"roi"|"ni"|"international"], url }]
 * A second map (`data-variants`) carries each variant's finish name, SKU and image for the
 * product strip. Both follow the variant picker via `variant:update`.
 *
 * Retailer order is shuffled every time the popup opens (Thomas, 2026-11-04), so no stockist
 * always sits at the top. It stays put while the popup is open — changing finish or region
 * doesn't reshuffle under the visitor's cursor.
 */

const PROGUS_ORIGIN = 'https://sl-widget.proguscommerce.com';
const PHONE = '(max-width: 749px)';

const REGION_ORDER = ['uk', 'roi', 'ni', 'international'];

/** Full names — rows ("Delivers to …") and the single-region line. */
/** @type {Record<string, string>} */
const REGION_LABELS = {
  uk: 'UK',
  roi: 'Republic of Ireland',
  ni: 'Northern Ireland',
  international: 'International',
};

/** Short names — the region switch, where space is tight. */
/** @type {Record<string, string>} */
const REGION_SWITCH_LABELS = {
  uk: 'UK',
  roi: 'ROI',
  ni: 'NI',
  international: 'International',
};

/** A retailer's identity in the stockist payload (handle; name as a fallback). */
const retailerKey = (/** @type {Record<string, any>} */ item) => String(item?.retailer || item?.name || '');

class WhereToBuy extends HTMLElement {
  #abort = new AbortController();
  /** @type {Record<string, Array<Record<string, any>>>} */
  #data = {};
  /** @type {Record<string, Record<string, string>>} */
  #variants = {};
  #variantId = '';
  #region = '';
  /** @type {HTMLIFrameElement | null} */
  #map = null;
  /** Phone map: the iframe takes the height Progus asks for (see #onMapMessage). */
  #mapAutoHeight = false;
  /** Retailer handle -> position, reshuffled on every open (#shuffle). */
  /** @type {Map<string, number>} */
  #order = new Map();

  connectedCallback() {
    this.#data = readJson(this.querySelector('[data-stockists]'));
    this.#variants = readJson(this.querySelector('[data-variants]'));
    this.#variantId = String(this.dataset.currentVariantId || '');

    const { signal } = this.#abort;
    const section = this.closest('.shopify-section');
    section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    this.querySelector('[data-tabs]')?.addEventListener('click', this.#onTabClick, { signal });
    this.querySelector('[data-regions]')?.addEventListener('click', this.#onRegionClick, { signal });
    this.querySelector('[data-show-map]')?.addEventListener('click', this.#onShowMap, { signal });
    window.addEventListener('message', this.#onMapMessage, { signal });
    // Horizon's dialog-component fires a non-bubbling `dialog:open` on itself after showModal.
    this.querySelector('dialog-component')?.addEventListener('dialog:open', this.#onOpen, { signal });

    this.#shuffle();
    this.#render();
  }

  #onOpen = () => {
    this.#shuffle();
    this.#renderRows();
  };

  /** Fisher–Yates over every retailer on the product (all finishes), so each order is
   *  equally likely and a retailer keeps its place when the visitor switches finish. */
  #shuffle() {
    const keys = [...new Set(Object.values(this.#data).flat().map(retailerKey))];
    for (let i = keys.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [keys[i], keys[j]] = [keys[j], keys[i]];
    }
    this.#order = new Map(keys.map((key, index) => [key, index]));
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  #onVariantUpdate = (/** @type {any} */ event) => {
    const id = event.detail?.resource?.id;
    if (id == null) return;
    this.#variantId = String(id);
    this.#region = ''; // let #render pick the first region the new finish has
    this.#render();
  };

  // Every handler below stops propagation: Horizon's dialog-component closes on a click it
  // judges "outside" the dialog, and a click on content we re-render reads as outside.
  #onTabClick = (/** @type {MouseEvent} */ event) => {
    const btn = /** @type {HTMLElement} */ (event.target)?.closest('[data-tab]');
    if (!(btn instanceof HTMLElement)) return;
    event.stopPropagation();
    this.#selectTab(btn.dataset.tab || 'online');
  };

  #onShowMap = (/** @type {MouseEvent} */ event) => {
    event.stopPropagation();
    this.#selectTab('map');
  };

  #onRegionClick = (/** @type {MouseEvent} */ event) => {
    const btn = /** @type {HTMLElement} */ (event.target)?.closest('[data-region]');
    if (!(btn instanceof HTMLElement)) return;
    event.stopPropagation();
    this.#region = btn.dataset.region || '';
    this.#renderRegions();
    this.#renderRows();
  };

  /** Progus posts { pslheight: '777px' } as its layout grows; only the phone map follows it. */
  #onMapMessage = (/** @type {MessageEvent} */ event) => {
    if (!this.#map || !this.#mapAutoHeight || event.origin !== PROGUS_ORIGIN) return;
    const height = event.data?.pslheight;
    if (height) this.#map.style.height = typeof height === 'number' ? `${height}px` : String(height);
  };

  #current() {
    const list = this.#data[this.#variantId];
    return Array.isArray(list) ? list : [];
  }

  #regionsPresent() {
    const set = new Set();
    for (const item of this.#current()) {
      (item.regions || []).forEach((/** @type {string} */ r) => set.add(r));
    }
    return REGION_ORDER.filter((r) => set.has(r));
  }

  #render() {
    const present = this.#regionsPresent();
    if (!present.includes(this.#region)) this.#region = present[0] || '';

    const count = this.#current().length;
    const hasAny = count > 0;
    setHidden(this.querySelector('[data-online-list]'), !hasAny);
    setHidden(this.querySelector('[data-empty]'), hasAny);

    const countEl = this.querySelector('[data-count]');
    if (countEl) countEl.textContent = hasAny ? ` · ${count} ${count === 1 ? 'retailer' : 'retailers'}` : '';

    this.#renderProduct();
    this.#renderRegions();
    this.#renderRows();
  }

  #renderProduct() {
    const meta = this.#variants[this.#variantId];
    if (!meta) return;
    const finish = this.querySelector('[data-finish]');
    if (finish) finish.textContent = [meta.title, meta.sku].filter(Boolean).join(' · ');
    const img = this.querySelector('[data-product-image]');
    if (img instanceof HTMLImageElement && meta.image) img.src = meta.image;
  }

  #renderRegions() {
    const present = this.#regionsPresent();
    const label = this.querySelector('[data-region-label]');
    const regions = this.querySelector('[data-regions]');
    if (label) {
      label.textContent =
        present.length === 1
          ? `Retailers stocking this finish, delivering to ${REGION_LABELS[present[0]]}`
          : 'Retailers stocking this finish, delivering to';
    }
    if (!(regions instanceof HTMLElement)) return;
    regions.hidden = present.length <= 1;
    // Rebuild only when the set of regions changes; otherwise flip state in place.
    const key = present.join(',');
    if (regions.dataset.key !== key) {
      regions.dataset.key = key;
      regions.innerHTML = present
        .map(
          (r) =>
            `<button type="button" class="how-to-buy__region" data-region="${r}" aria-pressed="false">${REGION_SWITCH_LABELS[r]}</button>`
        )
        .join('');
    }
    for (const btn of regions.querySelectorAll('[data-region]')) {
      if (btn instanceof HTMLElement) {
        btn.setAttribute('aria-pressed', btn.dataset.region === this.#region ? 'true' : 'false');
      }
    }
  }

  #renderRows() {
    const list = this.querySelector('[data-rows]');
    if (!list) return;
    const items = this.#current()
      .filter((item) => (item.regions || []).includes(this.#region))
      .sort((a, b) => (this.#order.get(retailerKey(a)) ?? 0) - (this.#order.get(retailerKey(b)) ?? 0));
    list.innerHTML = items.map((item) => row(item)).join('');
  }

  #selectTab(/** @type {string} */ tab) {
    // The map tab gets a taller (desktop: wider too) popup; CSS keys off this. On desktop the
    // map fills it rather than taking the height Progus asks for, so the popup never scrolls.
    const dialog = this.querySelector('dialog');
    if (dialog) dialog.dataset.tab = tab;
    for (const btn of this.querySelectorAll('[data-tab]')) {
      if (btn instanceof HTMLElement) btn.setAttribute('aria-selected', btn.dataset.tab === tab ? 'true' : 'false');
    }
    for (const panel of this.querySelectorAll('[data-panel]')) {
      if (panel instanceof HTMLElement) panel.hidden = panel.dataset.panel !== tab;
    }
    if (tab === 'map' && !this.#map) this.#afterResize(dialog, () => this.#loadMap());
  }

  /** Run `callback` once the popup has finished widening for the map tab, so Progus sets its
   *  starting view at the final size. No transition (phone, reduced motion) → straight away. */
  #afterResize(/** @type {HTMLDialogElement | null} */ dialog, /** @type {() => void} */ callback) {
    const duration = dialog ? parseFloat(getComputedStyle(dialog).transitionDuration) || 0 : 0;
    if (!dialog || !duration) {
      callback();
      return;
    }
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      dialog.removeEventListener('transitionend', onEnd);
      callback();
    };
    const onEnd = (/** @type {TransitionEvent} */ event) => {
      if (event.target === dialog && event.propertyName === 'max-width') finish();
    };
    dialog.addEventListener('transitionend', onEnd);
    setTimeout(finish, duration * 1000 + 100); // the width may not change (already wide), so no event
  }

  /** The Progus iframe only loads the first time the map tab opens — product pages stay light. */
  #loadMap() {
    if (this.#map) return;
    const holder = this.querySelector('[data-map]');
    if (!(holder instanceof HTMLElement) || !holder.dataset.src) return;
    const phone = window.matchMedia(PHONE).matches && Boolean(holder.dataset.srcMobile);
    this.#mapAutoHeight = phone;
    const frame = document.createElement('iframe');
    frame.src = (phone ? holder.dataset.srcMobile : holder.dataset.src) || '';
    frame.title = holder.dataset.title || 'Stockist map';
    frame.allow = 'geolocation';
    frame.setAttribute('allowfullscreen', '');
    frame.className = 'how-to-buy__map-frame';
    holder.appendChild(frame);
    this.#map = frame;
  }
}

function row(/** @type {Record<string, any>} */ item) {
  const name = escapeHtml(item.name || '');
  const url = safeUrl(item.url);
  const logo = item.logo
    ? `<span class="how-to-buy__row-logo"><img src="${escapeHtml(item.logo)}" alt="" loading="lazy" width="88" height="88"></span>`
    : `<span class="how-to-buy__row-logo how-to-buy__row-logo--text" aria-hidden="true">${name}</span>`;
  const regions = joinRegions(REGION_ORDER.filter((r) => (item.regions || []).includes(r)));
  return `<li>
      <a class="how-to-buy__row" href="${url}" target="_blank" rel="noopener noreferrer">
        ${logo}
        <span class="how-to-buy__row-body">
          <span class="how-to-buy__row-name">${name}</span>
          ${regions ? `<span class="how-to-buy__row-meta">Delivers to ${regions}</span>` : ''}
        </span>
        <span class="how-to-buy__row-cta">
          <span class="how-to-buy__row-cta-label">Visit retailer</span>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M5 2h7v7M12 2L3 11"/></svg>
        </span>
        <span class="visually-hidden">(opens in a new tab)</span>
      </a>
    </li>`;
}

/** ["uk","international"] → "UK & International"; three or more → "UK, NI & International". */
function joinRegions(/** @type {string[]} */ regions) {
  const names = regions.map((r) => REGION_LABELS[r]).filter(Boolean);
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} &amp; ${names[names.length - 1]}`;
}

function readJson(/** @type {Element | null} */ script) {
  try {
    return script && script.textContent ? JSON.parse(script.textContent) || {} : {};
  } catch {
    return {};
  }
}

function setHidden(/** @type {Element | null} */ el, /** @type {boolean} */ hidden) {
  if (el instanceof HTMLElement) el.hidden = hidden;
}

/** Escape a string for safe insertion into HTML text or a double-quoted attribute. */
function escapeHtml(/** @type {string} */ value) {
  return String(value).replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] || c
  );
}

/** Only allow http(s) links; anything else falls back to a no-op anchor. */
function safeUrl(/** @type {string} */ value) {
  const url = String(value || '');
  return /^https?:\/\//i.test(url) ? escapeHtml(url) : '#';
}

if (!customElements.get('where-to-buy')) {
  customElements.define('where-to-buy', WhereToBuy);
}
